import React from "react";
import { useAuth } from "../context/AuthContext";
import { getSessionOptions, requestSession, paySession, getMySessions, getAdminSessions, setSessionLink, adminConfirmSession, getEarnings, formatNaira } from "../lib/api";
import { AVATAR_HERO } from "../lib/constants";
import { Video, MessageCircle, Clock, Loader2, Lock, Calendar, CheckCircle2, Link2, ExternalLink, Wallet, Users, Repeat, CreditCard, Hourglass } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { id: "zoom", label: "Zoom (audio)", icon: Video, note: "Audio-only virtual link" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, note: "Voice call on WhatsApp" },
];

const STATUS_LABEL = {
  requested: { t: "Awaiting confirmation", c: "text-ink/50 bg-ink/5" },
  confirmed: { t: "Confirmed · pay to lock", c: "text-clay bg-clay/10" },
  paid: { t: "Paid · awaiting link", c: "text-ochre bg-ochre/10" },
  link_sent: { t: "Link ready", c: "text-leaf bg-leaf/10" },
  cancelled: { t: "Cancelled", c: "text-clay bg-clay/10" },
};

// Convert an ISO string to the value a <input type="datetime-local"> expects (local time).
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const Countdown = ({ target }) => {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return <span className="text-leaf font-bold">Starting now / in progress</span>;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const soon = diff <= 10 * 60000;
  return (
    <span className={`font-bold tabular-nums ${soon ? "text-clay" : "text-ink/70"}`}>
      {d > 0 ? `${d}d ` : ""}{d > 0 || h > 0 ? `${h}h ` : ""}{m}m {String(s).padStart(2, "0")}s
    </span>
  );
};

const EarningsDashboard = () => {
  const [e, setE] = React.useState(null);
  React.useEffect(() => {
    getEarnings().then(setE).catch(() => setE(null));
  }, []);
  if (!e) return null;
  const cards = [
    { label: "Total earnings", value: formatNaira(e.grand_total), accent: "text-clay", icon: Wallet },
    { label: "Subscriptions", value: formatNaira(e.subscription_total), sub: `${e.subscription_count} payments`, accent: "text-ochre", icon: Repeat },
    { label: "Sessions", value: formatNaira(e.sessions_total), sub: `${e.sessions_count} booked`, accent: "text-leaf", icon: Video },
    { label: "Active subscribers", value: e.active_subscribers, accent: "text-ink", icon: Users },
  ];
  return (
    <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-dashboard">
      {cards.map((c) => (
        <div key={c.label} className="bg-sand-light border border-border rounded-2xl p-5" data-testid={`earning-${c.label.split(" ")[0].toLowerCase()}`}>
          <c.icon size={18} className={c.accent} />
          <p className="font-display font-black text-2xl text-ink mt-3">{c.value}</p>
          <p className="text-xs text-ink/60">{c.label}{c.sub ? ` · ${c.sub}` : ""}</p>
        </div>
      ))}
    </div>
  );
};

const AdminPanel = ({ refresh }) => {
  const [rows, setRows] = React.useState([]);
  const [links, setLinks] = React.useState({});
  const [times, setTimes] = React.useState({});
  const [notes, setNotes] = React.useState({});

  const load = React.useCallback(async () => {
    try {
      const data = await getAdminSessions();
      setRows(data);
      setLinks(Object.fromEntries(data.map((b) => [b.id, b.meeting_link || ""])));
      setTimes(Object.fromEntries(data.map((b) => [b.id, toLocalInput(b.scheduled_at) || toLocalInput(b.preferred_at)])));
      setNotes(Object.fromEntries(data.map((b) => [b.id, b.admin_note || ""])));
    } catch {
      setRows([]);
    }
  }, []);

  React.useEffect(() => { load(); }, [load, refresh]);

  const confirm = async (id) => {
    const val = times[id];
    if (!val) return toast.error("Pick a date and time to confirm.");
    try {
      await adminConfirmSession(id, { scheduled_at: new Date(val).toISOString(), admin_note: notes[id]?.trim() || null });
      toast.success("Confirmed — the client has been asked to pay.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm.");
    }
  };

  const save = async (id) => {
    const val = (links[id] || "").trim();
    if (!val) return toast.error("Paste a meeting link first.");
    try {
      await setSessionLink(id, { meeting_link: val });
      toast.success("Link sent to the client!");
      load();
    } catch {
      toast.error("Could not save link.");
    }
  };

  if (rows.length === 0)
    return <p className="text-ink/50 mt-4" data-testid="admin-sessions-empty">No session requests yet.</p>;

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="admin-sessions-list">
      {rows.map((b) => {
        const needsConfirm = b.status === "requested" || b.status === "confirmed";
        const needsLink = b.status === "paid" || b.status === "link_sent";
        return (
          <div key={b.id} className="bg-sand-light border border-border rounded-2xl p-5" data-testid={`admin-booking-${b.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-clay bg-clay/10 rounded-full px-3 py-1">
                {b.channel} · {b.duration}min · {formatNaira(b.amount)}
              </span>
              <span className={`text-xs font-bold rounded-full px-3 py-1 ${STATUS_LABEL[b.status]?.c}`}>{STATUS_LABEL[b.status]?.t}</span>
            </div>
            <p className="text-sm text-ink mt-3 font-semibold">{b.name} · {b.email}</p>
            <p className="text-xs text-ink/60">
              {b.preferred_at ? `Preferred: ${new Date(b.preferred_at).toLocaleString()}` : "No preferred time given"}
              {b.whatsapp_number ? ` · WhatsApp: ${b.whatsapp_number}` : ""}
            </p>
            {b.scheduled_at && <p className="text-xs text-leaf font-semibold mt-1">Confirmed for {new Date(b.scheduled_at).toLocaleString()}</p>}
            {b.prep_note && <p className="text-xs text-ink/70 mt-2 bg-sand rounded-lg px-3 py-2" data-testid={`admin-prep-${b.id}`}><b>Prep note:</b> {b.prep_note}</p>}

            {needsConfirm && (
              <div className="mt-3 space-y-2" data-testid={`admin-confirm-block-${b.id}`}>
                <label className="text-xs font-semibold text-ink/60">Set / update the confirmed date &amp; time</label>
                <input
                  type="datetime-local"
                  value={times[b.id] || ""}
                  onChange={(e) => setTimes((p) => ({ ...p, [b.id]: e.target.value }))}
                  className="w-full rounded-lg bg-sand border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay/30"
                  data-testid={`admin-confirm-time-${b.id}`}
                />
                <input
                  value={notes[b.id] || ""}
                  onChange={(e) => setNotes((p) => ({ ...p, [b.id]: e.target.value }))}
                  placeholder="Note to client (optional)"
                  className="w-full rounded-lg bg-sand border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay/30"
                  data-testid={`admin-confirm-note-${b.id}`}
                />
                <button onClick={() => confirm(b.id)} className="w-full rounded-lg bg-clay text-white px-4 py-2 text-sm font-bold hover:bg-clay-hover flex items-center justify-center gap-2" data-testid={`admin-confirm-btn-${b.id}`}>
                  <CheckCircle2 size={16} /> {b.status === "confirmed" ? "Update & re-notify" : "Confirm & ask to pay"}
                </button>
              </div>
            )}

            {needsLink && (
              <div className="flex gap-2 mt-3">
                <input
                  value={links[b.id] ?? (b.meeting_link || "")}
                  onChange={(e) => setLinks((p) => ({ ...p, [b.id]: e.target.value }))}
                  placeholder="Paste Zoom / Meet link"
                  className="flex-1 rounded-lg bg-sand border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay/30"
                  data-testid={`admin-link-input-${b.id}`}
                />
                <button onClick={() => save(b.id)} className="rounded-lg bg-ink text-sand-light px-4 text-sm font-bold hover:opacity-90" data-testid={`admin-link-save-${b.id}`}>
                  Send
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MySessions = ({ refresh }) => {
  const [rows, setRows] = React.useState([]);
  const [paying, setPaying] = React.useState(null);

  const load = React.useCallback(() => {
    getMySessions().then(setRows).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { load(); }, [load, refresh]);

  const pay = async (id) => {
    setPaying(id);
    try {
      const { checkout_url } = await paySession(id);
      window.location.assign(checkout_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment.");
      setPaying(null);
    }
  };

  if (rows.length === 0) return null;
  const now = Date.now();
  return (
    <div className="mt-10">
      <h3 className="font-display font-bold text-xl text-ink">Your booked sessions</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="my-sessions-list">
        {rows.map((b) => {
          const upcoming = b.scheduled_at && new Date(b.scheduled_at).getTime() > now;
          const joinSoon = upcoming && new Date(b.scheduled_at).getTime() - now <= 10 * 60000;
          return (
            <div key={b.id} className="bg-sand-light border border-border rounded-2xl p-5" data-testid={`my-session-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-clay bg-clay/10 rounded-full px-3 py-1">{b.channel} · {b.duration}min</span>
                <span className={`text-xs font-bold rounded-full px-3 py-1 ${STATUS_LABEL[b.status]?.c}`} data-testid={`my-session-status-${b.id}`}>{STATUS_LABEL[b.status]?.t}</span>
              </div>
              <p className="text-sm text-ink/70 mt-3">
                {formatNaira(b.amount)} · {b.scheduled_at ? new Date(b.scheduled_at).toLocaleString() : (b.preferred_at ? `Preferred: ${new Date(b.preferred_at).toLocaleString()}` : "Time to be confirmed")}
              </p>

              {b.status === "requested" && (
                <p className="text-sm mt-2 flex items-center gap-2 text-ink/60" data-testid={`awaiting-${b.id}`}>
                  <Hourglass size={14} className="text-clay" /> We'll confirm a time, then you can pay.
                </p>
              )}

              {b.status === "confirmed" && (
                <button
                  onClick={() => pay(b.id)}
                  disabled={paying === b.id}
                  className="mt-3 w-full rounded-full bg-clay text-white font-bold px-5 py-3 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid={`pay-now-${b.id}`}
                >
                  {paying === b.id ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  Pay now · {formatNaira(b.amount)}
                </button>
              )}

              {b.admin_note && (b.status === "confirmed" || b.status === "paid" || b.status === "link_sent") && (
                <p className="text-xs text-ink/70 mt-2 bg-sand rounded-lg px-3 py-2">{b.admin_note}</p>
              )}

              {upcoming && (b.status === "paid" || b.status === "link_sent") && (
                <p className="text-sm mt-2 flex items-center gap-2" data-testid={`countdown-${b.id}`}>
                  <Clock size={14} className="text-clay" /> Starts in <Countdown target={b.scheduled_at} />
                </p>
              )}

              {b.meeting_link && (
                <a href={b.meeting_link} target="_blank" rel="noopener noreferrer" className={`mt-3 inline-flex items-center gap-2 text-sm font-bold rounded-full px-5 py-2.5 transition-transform hover:-translate-y-0.5 ${joinSoon ? "bg-clay text-white animate-pulse" : "text-leaf hover:underline px-0"}`} data-testid={`my-session-link-${b.id}`}>
                  <ExternalLink size={15} /> {joinSoon ? "Join now" : "Join session"}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SessionsSection = () => {
  const { user, setAuthOpen } = useAuth();
  const [opts, setOpts] = React.useState(null);
  const [channel, setChannel] = React.useState("zoom");
  const [duration, setDuration] = React.useState(30);
  const [preferredAt, setPreferredAt] = React.useState("");
  const [whatsapp, setWhatsapp] = React.useState("");
  const [prepNote, setPrepNote] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);

  React.useEffect(() => {
    getSessionOptions().then(setOpts).catch(() => setOpts(null));
  }, []);

  if (!opts) return null;

  const live = opts.sessions_live;
  const isPremium = opts.is_premium;
  const rows = opts.channels[channel] || [];
  const selected = rows.find((r) => r.duration === duration) || rows[0];
  const price = selected ? (isPremium ? selected.premium : selected.regular) : 0;

  const submit = async () => {
    if (!user) return setAuthOpen(true);
    if (!live) return;
    if (channel === "whatsapp" && !whatsapp.trim()) return toast.error("Enter your WhatsApp number.");
    setLoading(true);
    try {
      await requestSession({
        channel,
        duration,
        preferred_at: preferredAt ? new Date(preferredAt).toISOString() : null,
        whatsapp_number: channel === "whatsapp" ? whatsapp.trim() : null,
        prep_note: prepNote.trim() || null,
      });
      toast.success("Request sent! We'll confirm a time, then you can pay to lock it in.");
      setPreferredAt("");
      setPrepNote("");
      setRefresh((x) => x + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="sessions" className="relative scroll-mt-20 bg-sand-dark/40 border-y border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-clay">Live 1:1 Sessions</span>
            <h2 className="font-display font-black text-ink text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
              Talk directly with the mind behind Shabba Robo.
            </h2>
            <p className="text-ink/70 mt-4 leading-relaxed">
              Request a private, real-time session — Zoom audio or WhatsApp call. We'll agree a time with you first,
              and you only pay once it's confirmed.
              {isPremium && <span className="font-semibold text-leaf"> Premium members save {opts.discount_pct}%.</span>}
            </p>

            {!live && (
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink text-sand-light text-sm font-bold px-5 py-2.5" data-testid="sessions-coming-soon">
                <Lock size={15} /> Coming soon — bookings open after the 30-day free period
              </div>
            )}

            <div className="mt-8 flex items-center gap-4">
              <img src={AVATAR_HERO} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-clay/40" />
              <p className="text-sm text-ink/60 max-w-xs">Personal, human, and confidential — the real voice, not the AI.</p>
            </div>
          </div>

          {/* Request card */}
          <div className="bg-sand-light border border-border rounded-3xl p-6 sm:p-8" data-testid="sessions-booking-card">
            <div className="grid grid-cols-2 gap-3">
              {CHANNELS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setChannel(c.id)}
                  className={`rounded-2xl border p-4 text-left transition-colors ${channel === c.id ? "border-clay bg-clay/5" : "border-border hover:border-clay/40"}`}
                  data-testid={`session-channel-${c.id}`}
                >
                  <c.icon size={20} className="text-clay" />
                  <p className="font-bold text-ink mt-2 text-sm">{c.label}</p>
                  <p className="text-xs text-ink/50">{c.note}</p>
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-ink/70 mt-6">Choose duration</p>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {rows.map((r) => (
                <button
                  key={r.duration}
                  onClick={() => setDuration(r.duration)}
                  className={`rounded-xl border py-3 flex flex-col items-center transition-colors ${duration === r.duration ? "border-clay bg-clay/5" : "border-border hover:border-clay/40"}`}
                  data-testid={`session-duration-${r.duration}`}
                >
                  <Clock size={15} className="text-clay" />
                  <span className="font-bold text-ink text-sm mt-1">{r.duration === 60 ? "1 hr" : `${r.duration}m`}</span>
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-ink/70 mt-6 flex items-center gap-2"><Calendar size={15} className="text-clay" /> Preferred date &amp; time (optional)</p>
            <input
              type="datetime-local"
              value={preferredAt}
              onChange={(e) => setPreferredAt(e.target.value)}
              className="mt-2 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30"
              data-testid="session-preferred-input"
            />
            <p className="text-xs text-ink/40 mt-1">We'll confirm the exact time with you before any payment.</p>

            {channel === "whatsapp" && (
              <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="Your WhatsApp number (e.g. +234...)" className="mt-3 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30" data-testid="session-whatsapp-input" />
            )}

            <textarea
              value={prepNote}
              onChange={(e) => setPrepNote(e.target.value)}
              rows={2}
              placeholder="Briefly, what's it about? (optional — helps me prepare)"
              className="mt-3 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 resize-none"
              data-testid="session-prep-input"
            />

            <div className="mt-6 flex items-center justify-between">
              <div>
                {isPremium && selected && (
                  <span className="text-sm text-ink/40 line-through mr-2">{formatNaira(selected.regular)}</span>
                )}
                <span className="font-display font-black text-2xl text-ink" data-testid="session-price">{formatNaira(price)}</span>
                <span className="block text-xs text-ink/40">estimated · pay after we confirm</span>
              </div>
              <button
                onClick={submit}
                disabled={loading || !live}
                className="rounded-full bg-clay text-white font-bold px-7 py-3.5 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center gap-2"
                data-testid="session-book-btn"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                {live ? (user ? "Request session" : "Sign in to request") : "Coming soon"}
              </button>
            </div>
          </div>
        </div>

        {user && <MySessions refresh={refresh} />}

        {user?.role === "admin" && (
          <div className="mt-12" data-testid="admin-sessions-panel">
            <h3 className="font-display font-bold text-2xl text-ink flex items-center gap-2">
              <Link2 size={20} className="text-clay" /> Admin · Manage bookings
            </h3>
            <div className="mt-4">
              <h4 className="font-display font-bold text-lg text-ink flex items-center gap-2"><Wallet size={18} className="text-clay" /> Earnings</h4>
              <EarningsDashboard />
            </div>
            <p className="text-ink/60 text-sm mt-6">Review each request, confirm a date &amp; time (the client is then asked to pay), and once paid, paste the Zoom/Meet link.</p>
            <AdminPanel refresh={refresh} />
          </div>
        )}
      </div>
    </section>
  );
};
